import React, { useState, useRef, useEffect, useCallback } from 'react';
import { AlignIcon, ALIGN_LINES_BY_COMMAND, ALIGN_LABELS } from './AlignIcons';
import ColorPicker from './ColorPicker';

/**
 * Lightweight contentEditable rich-text cell editor for table cells.
 * Supports bold, italic, underline, text color via a compact toolbar.
 * Also supports per-cell background and text color overrides.
 * Stores content as HTML string.
 */
const FONT_SIZES = [7, 8, 9, 10, 11, 12, 13, 14, 16, 18, 20, 24, 28, 32];
const FONT_FAMILIES = ['Helvetica', 'Times-Roman', 'Courier', 'Calibri', 'Arial'];

export default function InlineCellEditor({ value, onChange, textColor, cellBg, cellTextColor, cellFontSize, cellFont, onCellStyleChange }) {
  const ref = useRef(null);
  const [focused, setFocused] = useState(false);
  const lastHtml = useRef(value || '');

  const initialValue = useRef(value || '');

  // Set initial content on mount
  useEffect(() => {
    if (ref.current && !ref.current.innerHTML) {
      ref.current.innerHTML = initialValue.current;
      lastHtml.current = initialValue.current;
    }
  }, []);

  // Sync external value changes when not focused
  useEffect(() => {
    if (!focused && ref.current) {
      const current = ref.current.innerHTML;
      if (current !== (value || '')) {
        ref.current.innerHTML = value || '';
        lastHtml.current = value || '';
      }
    }
  }, [value, focused]);

  const emitChange = useCallback(() => {
    if (!ref.current) return;
    const html = ref.current.innerHTML;
    // Normalize: if only whitespace/empty tags, treat as empty
    const text = ref.current.textContent || '';
    const normalized = text.trim() === '' ? '' : html;
    if (normalized !== lastHtml.current) {
      lastHtml.current = normalized;
      onChange(normalized);
    }
  }, [onChange]);

  const exec = (cmd, val) => {
    document.execCommand(cmd, false, val);
    ref.current?.focus();
    emitChange();
  };

  const isActive = (cmd) => {
    try { return document.queryCommandState(cmd); } catch { return false; }
  };

  const tbBtn = (label, cmd, title) => (
    <button
      key={cmd}
      type="button"
      title={title}
      onMouseDown={e => { e.preventDefault(); exec(cmd); }}
      style={{
        padding: '1px 5px', border: 'none', borderRadius: 3, cursor: 'pointer',
        background: isActive(cmd) ? 'var(--primary)' : 'transparent',
        color: isActive(cmd) ? '#fff' : 'var(--text-secondary)',
        fontSize: 11, fontWeight: 600, lineHeight: 1, minWidth: 20,
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center'
      }}
    >{label}</button>
  );

  const wrapperRef = useRef(null);
  const blurTimeout = useRef(null);

  const handleFocus = () => {
    if (blurTimeout.current) { clearTimeout(blurTimeout.current); blurTimeout.current = null; }
    setFocused(true);
  };

  const handleBlur = () => {
    blurTimeout.current = setTimeout(() => {
      // The colour picker's panel is portalled to document.body, so focus
      // landing in it is outside the wrapper while still being this cell's
      // own UI. Treat it as inside, or typing a hex value closes the editor.
      const active = document.activeElement;
      const inPicker = active && active.closest && active.closest('[data-colorpicker-panel]');
      if (wrapperRef.current && !wrapperRef.current.contains(active) && !inPicker) {
        setFocused(false);
        emitChange();
      }
      blurTimeout.current = null;
    }, 50);
  };

  // Effective text color: cell-level > row-level > default
  const effectiveTextColor = cellTextColor || textColor || 'var(--text)';

  return (
    <div ref={wrapperRef} onBlur={handleBlur} style={{ position: 'relative', minHeight: 28, background: cellBg || undefined }}>
      <div
        ref={ref}
        contentEditable
        suppressContentEditableWarning
        onFocus={handleFocus}
        onInput={emitChange}
        onPaste={e => {
          e.preventDefault();
          const text = e.clipboardData.getData('text/plain');
          document.execCommand('insertText', false, text);
        }}
        style={{
          minHeight: 24, padding: '3px 6px', fontSize: cellFontSize || 12, outline: 'none',
          color: effectiveTextColor, lineHeight: 1.4, wordBreak: 'break-word'
        }}
      />
      {focused && (
        <div
          onMouseDown={e => { if (e.target.tagName !== 'SELECT' && e.target.tagName !== 'INPUT') e.preventDefault(); }}
          style={{
            position: 'absolute', bottom: '100%', left: 0, zIndex: 110,
            display: 'flex', gap: 1, padding: '2px 4px', alignItems: 'center',
            background: 'var(--bg-card)', border: '1px solid var(--border)',
            borderRadius: 'var(--radius)', boxShadow: 'var(--shadow-lg)',
            marginBottom: 2
          }}
        >
          {tbBtn('B', 'bold', 'Bold')}
          {tbBtn(<em>I</em>, 'italic', 'Italic')}
          {tbBtn(<u>U</u>, 'underline', 'Underline')}
          <div style={{ width: 1, height: 14, background: 'var(--border)', margin: '0 2px' }} />
          {['justifyLeft', 'justifyCenter', 'justifyRight', 'justifyFull'].map(cmd => (
            <button key={cmd} type="button" title={ALIGN_LABELS[cmd]}
              onMouseDown={e => { e.preventDefault(); exec(cmd); }}
              style={{ padding: '1px 4px', border: 'none', borderRadius: 3, cursor: 'pointer', background: 'transparent', color: 'var(--text-secondary)', fontSize: 11, lineHeight: 1, minWidth: 18, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
              <AlignIcon lines={ALIGN_LINES_BY_COMMAND[cmd]} size={12} />
            </button>
          ))}
          <div style={{ width: 1, height: 14, background: 'var(--border)', margin: '0 2px' }} />
          {/* preserveFocus matters most here: exec() is document.execCommand,
              which applies to the current selection. Blur the editable while
              picking and the command lands on nothing. */}
          <ColorPicker
            label="Inline Text Color"
            size={18}
            preserveFocus
            allowNone={false}
            value={null}
            onChange={c => c && exec('foreColor', c)}
          />
          {onCellStyleChange && (
            <>
              <div style={{ width: 1, height: 14, background: 'var(--border)', margin: '0 3px' }} />
              <select
                title="Cell Font"
                value={cellFont || ''}
                onMouseDown={e => e.stopPropagation()}
                onChange={e => onCellStyleChange('font', e.target.value || null)}
                style={{
                  padding: '1px 2px', fontSize: 10, border: '1px solid var(--border)',
                  borderRadius: 3, background: 'var(--bg-input)', color: 'var(--text)',
                  cursor: 'pointer', width: 62
                }}
              >
                <option value="">Font</option>
                {FONT_FAMILIES.map(f => <option key={f} value={f}>{f}</option>)}
              </select>
              <select
                title="Cell Font Size"
                value={cellFontSize || ''}
                onMouseDown={e => e.stopPropagation()}
                onChange={e => onCellStyleChange('fontSize', e.target.value ? Number(e.target.value) : null)}
                style={{
                  padding: '1px 2px', fontSize: 10, border: '1px solid var(--border)',
                  borderRadius: 3, background: 'var(--bg-input)', color: 'var(--text)',
                  cursor: 'pointer', width: 42
                }}
              >
                <option value="">Def</option>
                {FONT_SIZES.map(s => <option key={s} value={s}>{s}pt</option>)}
              </select>
              <div style={{ width: 1, height: 14, background: 'var(--border)', margin: '0 3px' }} />
              <div style={{ display: 'flex', alignItems: 'center', gap: 2 }} title="Cell Background">
                <span style={{ fontSize: 9, color: 'var(--text-secondary)', fontWeight: 600 }}>BG</span>
                <ColorPicker
                  label="Cell Background"
                  size={18}
                  value={cellBg || null}
                  onChange={c => onCellStyleChange('bg', c)}
                />
                {cellBg && (
                  <button
                    onMouseDown={e => { e.preventDefault(); e.stopPropagation(); onCellStyleChange('bg', null); }}
                    style={{ border: 'none', background: 'none', cursor: 'pointer', color: 'var(--text-secondary)', fontSize: 10, padding: 0, lineHeight: 1 }}
                    title="Clear background"
                  >x</button>
                )}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 2 }} title="Cell Text Color">
                <span style={{ fontSize: 9, color: 'var(--text-secondary)', fontWeight: 600 }}>Txt</span>
                <ColorPicker
                  label="Cell Text Color"
                  size={18}
                  value={cellTextColor || null}
                  onChange={c => onCellStyleChange('textColor', c)}
                />
                {cellTextColor && (
                  <button
                    onMouseDown={e => { e.preventDefault(); e.stopPropagation(); onCellStyleChange('textColor', null); }}
                    style={{ border: 'none', background: 'none', cursor: 'pointer', color: 'var(--text-secondary)', fontSize: 10, padding: 0, lineHeight: 1 }}
                    title="Clear text color"
                  >x</button>
                )}
              </div>
            </>
          )}
        </div>
      )}
      <style>{`
        [contenteditable] b, [contenteditable] strong { font-weight: 700; }
        [contenteditable] i, [contenteditable] em { font-style: italic; }
        [contenteditable] u { text-decoration: underline; }
      `}</style>
    </div>
  );
}
